import React, { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { motion } from 'framer-motion';
import { TYPEFORM_URL } from '@/data/mock';
import { Send, CheckCircle, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GlobalDemoForm() {
  const { currentTheme: t, theme } = useTheme();
  const [formData, setFormData] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error('Error al enviar');
      setSubmitted(true);
    } catch (err) {
      setError('Error al enviar. Intenta de nuevo o usa el formulario alternativo.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-16 lg:py-24" style={{ background: theme === 'light' ? '#25255B' : t.bgSecondary }}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}>
            <CheckCircle size={64} className="mx-auto mb-6" style={{ color: '#4ADE80' }} />
          </motion.div>
          <h2 className="text-3xl font-bold mb-4" style={{ color: '#FFFFFF', fontFamily: 'Manrope, sans-serif' }}>
            ¡Gracias por tu interés!
          </h2>
          <p className="text-lg" style={{ color: '#CBD5E1' }}>
            Nos pondremos en contacto contigo muy pronto.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24" style={{ background: theme === 'light' ? '#25255B' : t.bgSecondary }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Text */}
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl lg:text-4xl font-bold mb-6"
              style={{ color: '#FFFFFF', fontFamily: 'Manrope, sans-serif' }}
            >
              ¿Listo para transformar tu empresa?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-lg mb-8 leading-relaxed"
              style={{ color: '#CBD5E1', fontFamily: 'Inter, sans-serif' }}
            >
              Solicita una demo personalizada y descubre cómo Motivy puede mejorar el engagement, bienestar y productividad de tu equipo.
            </motion.p>
            <div className="space-y-4">
              {[
                'Implementación en menos de 2 semanas',
                'Acompañamiento estratégico continuo',
                '+300K usuarios activos confían en nosotros',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <ArrowRight size={16} style={{ color: '#F05864' }} />
                  <span className="text-sm" style={{ color: '#CBD5E1' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="p-8 rounded-2xl space-y-5" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#CBD5E1' }}>Nombre *</label>
                  <input
                    type="text" name="name" required value={formData.name} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-colors duration-200"
                    style={{ background: 'rgba(255,255,255,0.08)', color: '#FFFFFF', border: '1px solid rgba(255,255,255,0.15)' }}
                    onFocus={(e) => e.target.style.borderColor = '#F05864'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
                    placeholder="Tu nombre"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#CBD5E1' }}>Email corporativo *</label>
                  <input
                    type="email" name="email" required value={formData.email} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-colors duration-200"
                    style={{ background: 'rgba(255,255,255,0.08)', color: '#FFFFFF', border: '1px solid rgba(255,255,255,0.15)' }}
                    onFocus={(e) => e.target.style.borderColor = '#F05864'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
                    placeholder="tu@empresa.com"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#CBD5E1' }}>Empresa *</label>
                  <input
                    type="text" name="company" required value={formData.company} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-colors duration-200"
                    style={{ background: 'rgba(255,255,255,0.08)', color: '#FFFFFF', border: '1px solid rgba(255,255,255,0.15)' }}
                    onFocus={(e) => e.target.style.borderColor = '#F05864'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
                    placeholder="Nombre de tu empresa"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#CBD5E1' }}>Teléfono</label>
                  <input
                    type="tel" name="phone" value={formData.phone} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-colors duration-200"
                    style={{ background: 'rgba(255,255,255,0.08)', color: '#FFFFFF', border: '1px solid rgba(255,255,255,0.15)' }}
                    onFocus={(e) => e.target.style.borderColor = '#F05864'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
                    placeholder="+1 234 567 890"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: '#CBD5E1' }}>Mensaje</label>
                <textarea
                  name="message" rows={3} value={formData.message} onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg text-sm outline-none resize-none transition-colors duration-200"
                  style={{ background: 'rgba(255,255,255,0.08)', color: '#FFFFFF', border: '1px solid rgba(255,255,255,0.15)' }}
                  onFocus={(e) => e.target.style.borderColor = '#F05864'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
                  placeholder="Cuéntanos sobre tu empresa y necesidades..."
                />
              </div>
              {error && <p className="text-sm" style={{ color: '#F05864' }}>{error}</p>}
              <button
                type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg text-sm font-semibold text-white transition-colors duration-200"
                style={{ background: loading ? '#999' : '#F05864' }}
                onMouseEnter={(e) => { if(!loading) e.currentTarget.style.background = '#D64550'; }}
                onMouseLeave={(e) => { if(!loading) e.currentTarget.style.background = '#F05864'; }}
              >
                {loading ? 'Enviando...' : <><Send size={16} /> Solicitar demo</>}
              </button>
              <p className="text-center text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                También puedes <a href={TYPEFORM_URL} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#93B5E8' }}>completar nuestro formulario externo</a>
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
